import { useCallback, useEffect, useState } from 'react';
import { useI18n, type CopyKey } from './i18n';
import { getMailState, isError, retryMail, setReportsOptOut, type MailState } from './api';

/**
 * Whether mail reaches this learner, and whether they want the part they can stop (F-106).
 *
 * **The bouncing half is the reason this panel exists, and it is the only place a learner
 * can find it out.** `POST /api/auth/email/start` deliberately answers "we sent it" whether
 * or not anything was sent — telling a caller which addresses this account can reach would
 * disclose who is verified in an AWS account shared with a live business — so a learner
 * whose mailbox is refusing our mail sees a sign-in screen that looks like it worked and a
 * code that never arrives. Without this panel there is no difference, from where they are
 * standing, between a broken mailbox and a broken product.
 *
 * It shows the mail server's own words. "Mail to this address is bouncing" tells somebody
 * nothing they can act on; `550 5.1.1 user unknown` tells them the mailbox name is wrong,
 * and `452 over quota` tells them to empty it.
 *
 * **Nothing is rendered when the account has no address.** A panel about mail to an address
 * that does not exist is a panel with no true sentence in it, and the summaries preference
 * has nowhere to be delivered either. It appears when `EmailAccess` above has been used.
 *
 * Beside `EmailAccess` rather than inside it, because that panel is a *form* for adding an
 * address and this is a *state* of one that exists — folding them together would mean the
 * form's stages had to account for a bounce, and a learner mid-way through typing a new
 * address would be told about the old one's mailbox.
 */

const BOUNCE_COPY: Record<string, CopyKey> = {
  hard_bounce: 'mail.bouncing.hard',
  complaint: 'mail.bouncing.complaint',
};

const RETRY_PROBLEM: Record<string, CopyKey> = {
  too_soon: 'mail.retry.tooSoon',
  not_suppressed: 'mail.retry.notSuppressed',
  no_address: 'mail.retry.noAddress',
};

export default function Mail() {
  const { t } = useI18n();
  const [state, setState] = useState<MailState>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<CopyKey>();
  const [retried, setRetried] = useState(false);

  const fill = (key: CopyKey, values: Record<string, string>): string =>
    t(key).replace(/\{(\w+)\}/g, (_, name: string) => values[name] ?? '');

  const refresh = useCallback(async () => {
    const result = await getMailState();
    setLoading(false);
    // A failed read leaves `state` undefined, which renders nothing at all. That is the
    // right degradation: this panel is an explanation, and an explanation nobody can be
    // sure of is worse than silence on a screen that has a working sign-in on it already.
    if (isError(result)) return;
    setState(result);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function toggleReports(wanted: boolean): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    // `reportsOptOut` is the stored field and the switch is phrased the other way round —
    // people turn things *on*, not off. Inverting here rather than in the API keeps the
    // stored meaning stable: a missing field means "not opted out", which is the safe
    // default for a value a bounce handler also writes.
    const result = await setReportsOptOut(!wanted);
    setBusy(false);
    if (isError(result)) {
      setProblem('mail.error.generic');
      return;
    }
    setState(result);
  }

  async function tryAgain(): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    setRetried(false);
    const result = await retryMail();
    setBusy(false);
    if (isError(result)) {
      setProblem(RETRY_PROBLEM[result.error ?? ''] ?? 'mail.error.generic');
      // The state may have moved under us — somebody signing in on their phone clears a
      // suppression too — so the refusal is followed by a re-read rather than leaving the
      // screen claiming something that is no longer true.
      void refresh();
      return;
    }
    setState(result);
    setRetried(true);
  }

  if (loading) {
    return (
      <section className="panel">
        <h2>{t('mail.title')}</h2>
        <p className="muted">{t('mail.checking')}</p>
      </section>
    );
  }

  if (!state?.email) return null;

  return (
    <section className="panel">
      <h2>{t('mail.title')}</h2>
      <p className="muted">{t('mail.body')}</p>

      {state.suppressed && (
        <div className="field">
          <p className="problem" role="alert">
            {fill(BOUNCE_COPY[state.reason ?? ''] ?? 'mail.bouncing.hard', { email: state.email })}
          </p>
          {state.diagnostic && (
            <small>{fill('mail.bouncing.diagnostic', { diagnostic: state.diagnostic })}</small>
          )}
          <div className="actions">
            <button
              type="button"
              className="button"
              onClick={() => void tryAgain()}
              // Offered only while the cooldown has passed. The limit is on the clearing
              // rather than on the sending because this is where it can be explained, and
              // one press spends one real bounce against a shared sending reputation.
              disabled={busy || !state.canRetry}
            >
              {busy ? t('mail.retry.working') : t('mail.retry')}
            </button>
            {retried && (
              <span className="muted" role="status">
                {t('mail.retry.done')}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="form">
        <div className="field">
          <label className="switch">
            <input
              type="checkbox"
              checked={!state.reportsOptOut}
              disabled={busy}
              onChange={(e) => void toggleReports(e.target.checked)}
            />
            <span>{state.reportsOptOut ? t('mail.reports.off') : t('mail.reports.on')}</span>
          </label>
          <small>{t('mail.reports.label')}</small>
        </div>
        {/*
          Said plainly that nothing is sent yet.

          The preference is real and stored, and the machinery behind it — the
          `List-Unsubscribe` headers, the one-click endpoint — is real too. What does not
          exist yet is anything that sends a summary. A switch that implied otherwise would
          have a learner waiting for mail that is not coming, which is the same failure a
          bouncing address produces and the one this panel is here to end.
        */}
        <small className="muted">{t('mail.reports.hint')}</small>
      </div>

      {problem && (
        <p className="problem" role="alert">
          {t(problem)}
        </p>
      )}
    </section>
  );
}
